import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { AuthService } from '../../services/auth.service'
import { ClientContextService } from '@app/core/services/client-context.service'
import { MessageService } from 'primeng/api'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'app-saml-callback',
  templateUrl: './saml-callback.component.html',
  styleUrls: ['./saml-callback.component.scss'],
  standalone: false,
})
export class SamlCallbackComponent implements OnInit {
  errorMessage: string = ''
  isError: boolean = false

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private clientContextService: ClientContextService,
    private messageService: MessageService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Get token from query params
    this.route.queryParams.subscribe((params) => {
      const token = params['token'] as string | undefined

      // Get clientId from route hierarchy (could be in parent or parent.parent)
      let clientId = this.route.parent?.snapshot.params['clientId'] as string | undefined
      if (!clientId && this.route.parent?.parent) {
        clientId = this.route.parent.parent.snapshot.params['clientId'] as string | undefined
      }

      // Also try to extract from URL path as fallback
      if (!clientId) {
        const urlMatch = this.router.url.match(/\/org\/([^/]+)/)
        clientId = urlMatch ? urlMatch[1] : undefined
      }

      if (token && clientId) {
        this.authService.setToken(token)
        this.clientContextService.setClientId(clientId)

        // Fetch user details and navigate to dashboard
        this.authService.getMe().subscribe({
          next: (response) => {
            this.authService.user = response.data ?? null
            this.router.navigate([`/org/${clientId}/dashboard`])
          },
          error: (err: { status?: number; message?: string; error?: { message?: string } } | null | undefined) => {
            console.error('Failed to fetch user details after SAML login:')
            console.error('Error details:', err)
            console.error('Error status:', err?.status)
            console.error('Error message:', err?.error?.message || err?.message)
            console.error('Token set:', !!this.authService.getToken())
            console.error('ClientId:', clientId)

            this.isError = true

            // More specific error message based on error type
            if (err?.status === 500) {
              this.errorMessage = 'Server error occurred. The backend failed to process your SAML authentication. Please contact support.'
            } else if (err?.status === 401 || err?.status === 403) {
              this.errorMessage = 'Authentication failed. The session token may be invalid.'
            } else {
              this.errorMessage = this.translate.instant('AUTH.ERRORS.SAML_LOGIN_FAILED.MESSAGE')
            }

            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('AUTH.ERRORS.SAML_LOGIN_FAILED.TITLE'),
              detail: this.errorMessage,
              life: 5000,
            })

            // Redirect after showing error
            setTimeout(() => {
              this.router.navigate([`/org/${clientId}/auth/login`])
            }, 3000)
          },
        })
      } else {
        // Invalid callback - redirect to login
        console.warn('SAML callback missing token or clientId', { token: !!token, clientId })
        if (clientId) {
          this.router.navigate([`/org/${clientId}/auth/login`])
        } else {
          this.router.navigate(['/auth/login'])
        }
      }
    })
  }
}
